import { Component, inject, OnInit } from '@angular/core';
import { etiquetaVaca } from '@shared/utils/etiqueta-vaca';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { AppFormComponent } from '@shared/components/ui/list-components';
import { CreationButtons } from '@shared/components/ui/creation-buttons/creation-buttons';
import { ErrorService } from '@shared/services/error.service';
import { InputFieldComponent } from '@shared/components/form/input/input-field.component';
import { LabelComponent } from '@shared/components/form/label/label.component';
import { SelectFieldComponent, SelectOption } from '@shared/components/form/select/select-field.component';
import { SnackbarService } from '@shared/services/snackbar.service';
import { forkJoin } from 'rxjs';
import { Modal } from '@shared/components/common/modal/modal';

import { Cliente } from '../../../crm/clientes/clientes.interface';
import { AnimalResumen, StockVitrificadoPayload } from '../embriones.interface';
import { EmbrionesService } from '../embriones.service';

interface DialogData {
  clienteId?: number | null;
}

@Component({
  selector: 'app-crear-stock-vitrificado',
  imports: [
    AppFormComponent,
    CreationButtons,
    InputFieldComponent,
    LabelComponent,
    ReactiveFormsModule,
    SelectFieldComponent,
    Modal,
  ],
  templateUrl: './crear-stock-vitrificado.html',
})
export class CrearStockVitrificado implements OnInit {
  saveForm!: FormGroup;
  loading = false;
  loadingCombos = true;

  clientes: Cliente[] = [];
  donantes: AnimalResumen[] = [];
  toros: AnimalResumen[] = [];
  // Issue #20 (2026-09-15): el raque es texto libre; el combo del paramétrico
  // ya no se carga.
  // raques: ParamResumen[] = [];

  get clientesOptions(): SelectOption[] {
    return this.clientes.map(cliente => ({ value: cliente.id, label: cliente.persona.nombre_generico }));
  }

  get donantesOptions(): SelectOption[] {
    return this.donantes.map(donante => ({ value: donante.id, label: etiquetaVaca(donante, true) }));
  }

  get torosOptions(): SelectOption[] {
    return this.toros.map(toro => ({ value: toro.id, label: toro.hbp ? `${toro.nombre} (${toro.hbp})` : toro.nombre }));
  }

  // get raquesOptions(): SelectOption[] {
  //   return this.raques.map(raque => ({ value: raque.id, label: raque.nombre }));
  // }

  private fB = inject(FormBuilder);
  private embrionesService = inject(EmbrionesService);
  private errorService = inject(ErrorService);
  private snackbarService = inject(SnackbarService);
  private dialogRef = inject(MatDialogRef<CrearStockVitrificado>);
  private dialogData: DialogData = inject(MAT_DIALOG_DATA);

  ngOnInit(): void {
    this.makeForm();
    this.getCombos();
  }

  private makeForm(): void {
    this.saveForm = this.fB.group({
      cli_cliente_id: [this.dialogData.clienteId ?? '', Validators.required],
      donante_id: ['', Validators.required],
      toro_id: ['', Validators.required],
      raque: [''],
      fecha_opu: ['', Validators.required],
      fecha_vit: ['', Validators.required],
      nro_embriones: ['', [Validators.required, Validators.min(0)]],
      fecha_te: [''],
      te: [0, [Validators.min(0)]],
      descarte: [0, [Validators.min(0)]],
    });

    this.saveForm.get('cli_cliente_id')?.valueChanges.subscribe(value => {
      this.donantes = [];
      this.saveForm.patchValue({ donante_id: '' }, { emitEvent: false });

      if (value) {
        this.getDonantes(Number(value));
      }
    });
  }

  private getCombos(): void {
    this.loadingCombos = true;
    const clientes$ = this.embrionesService.comboClientes();
    const toros$ = this.embrionesService.getToros();

    // El OPS ya no se carga acá: identifica cada pajuela, y las pajuelas del stock
    // manual se marcan después, desde el detalle.
    forkJoin([clientes$, toros$]).subscribe({
      next: ([clientesResponse, torosResponse]) => {
        this.clientes = clientesResponse.data || [];
        this.toros = torosResponse.data || [];
      },
      error: (error) => {
        this.errorService.handleError(error);
      },
    }).add(() => {
      this.loadingCombos = false;
    });

    if (this.dialogData.clienteId) {
      this.getDonantes(this.dialogData.clienteId);
    }
  }

  private getDonantes(clienteId: number): void {
    this.embrionesService.getDonantes(clienteId).subscribe({
      next: (response) => {
        this.donantes = response.data || [];
      },
      error: (error) => {
        this.errorService.handleError(error);
      },
    });
  }

  create(): void {
    if (this.saveForm.invalid) {
      return;
    }

    this.loading = true;
    this.embrionesService.createStockVitrificado(this.saveForm.value as StockVitrificadoPayload).subscribe({
      next: (response) => {
        if (response.status) {
          this.snackbarService.showSnackBar('Stock vitrificado creado correctamente');
          this.dialogRef.close(true);
        }
      },
      error: (error) => {
        this.errorService.handleError(error);
      },
    }).add(() => {
      this.loading = false;
    });
  }

  cancel(): void {
    this.saveForm.reset();
    this.dialogRef.close();
  }
}
